import { useEffect, useState } from "react";
import { useNotification } from "@hooks/useNotifications";
import { workspacesService } from "features/workspaces/services/workspaces-service";
import type { ISimilarTranslationItemsFetchParams, ISimilarTranslationItems } from "features/workspaces/types";

export const useFetchSimilarTranslationsInProject = (
  fetchParams: ISimilarTranslationItemsFetchParams,
  { idInFile, fileName }: { idInFile: string; fileName: string },
  setIsLoading: (loading: boolean) => void,
): {
  similarTranslations: ISimilarTranslationItems;
} => {
  const [similarTranslations, setSimilarTranslations] = useState<ISimilarTranslationItems>([]);

  const { showError } = useNotification();

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await workspacesService.getSimilarTranslationItems(fetchParams, abortController);

        if (!abortController.signal.aborted && Array.isArray(response)) {
          // Remove the current item from the list
          const list = response.filter(item => !(item.idInFile === idInFile && item.fileName === fileName));
          setSimilarTranslations(list);
        }
      } catch (error) {
        if (!abortController?.signal.aborted) {
          if (error?.response?.status !== 404) {
            console.error(error);

            showError("Failed to fetch similar translation items.");
          }

          setSimilarTranslations([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [fetchParams]);

  return {
    similarTranslations,
  };
};
